import { useCallback, useEffect, useRef, useState } from "react";
import { Delete, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

// Task #614 — Manual dialpad.
// Lets a rep type/paste an arbitrary number and place a call through the
// exact same path click-to-call uses: the global `entertaincrm:dialer:open`
// CustomEvent, which DialerHost turns into a MiniDialer (browser SDK leg or
// "ring my phone" bridge, depending on the rep's routing preference).
// Task #615 — once the typed number forms a dialable E.164 destination, we
// look it up via GET /api/dialer/resolve-caller (same resolver the inbound
// screen-pop uses). If it matches a known lead/client/contact, the match is
// shown above the Call button and the placed call carries the leadId/clientId
// so it lands on the record's timeline exactly like click-to-call.
// Unmatched numbers behave as before — no link, no error.

const DIAL_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"] as const;

const KEY_LETTERS: Record<string, string> = {
  "2": "ABC",
  "3": "DEF",
  "4": "GHI",
  "5": "JKL",
  "6": "MNO",
  "7": "PQRS",
  "8": "TUV",
  "9": "WXYZ",
};

/** Strip everything the dialpad can't hold. A leading "+" is preserved. */
export function sanitizeDialInput(input: string): string {
  const plus = input.trim().startsWith("+");
  const rest = input.replace(/[^0-9*#]/g, "");
  return (plus ? "+" : "") + rest;
}

/**
 * Convert whatever the rep typed into the E.164 form the call path expects.
 * Defaults to US (+1) for bare 10-digit numbers. Returns null when the input
 * can't be dialed (too short, or contains DTMF-only characters).
 */
export function toE164(input: string, defaultCountryCode = "1"): string | null {
  const cleaned = sanitizeDialInput(input);
  if (!cleaned) return null;
  // * and # are DTMF tones, not dialable digits in an outbound destination.
  if (/[*#]/.test(cleaned)) return null;

  const hasPlus = cleaned.startsWith("+");
  let digits = cleaned.replace(/\D/g, "");
  if (!digits) return null;

  if (!hasPlus) {
    // International access prefix typed out longhand: 011 44 20 ...
    if (digits.startsWith("011") && digits.length > 3) {
      digits = digits.slice(3);
    } else if (digits.length === 10) {
      digits = defaultCountryCode + digits;
    } else if (digits.length === 11 && digits.startsWith(defaultCountryCode)) {
      // already country-coded
    } else if (digits.length < 11) {
      // Too short to be a full number without an explicit country code.
      return null;
    }
  }

  // NANP numbers are exactly 11 digits (1 + 10); everything else follows the
  // ITU E.164 bounds of 8–15 digits.
  if (digits.startsWith("1")) {
    if (digits.length !== 11) return null;
  } else if (digits.length < 8 || digits.length > 15) {
    return null;
  }

  return `+${digits}`;
}

/** Live, as-you-type formatting for the number display. */
export function formatDialDisplay(input: string): string {
  const cleaned = sanitizeDialInput(input);
  if (!cleaned) return "";

  const hasPlus = cleaned.startsWith("+");
  const body = hasPlus ? cleaned.slice(1) : cleaned;

  // Anything with DTMF characters is shown verbatim.
  if (/[*#]/.test(body)) return cleaned;

  if (!hasPlus) {
    // Domestic (US/Canada) as-you-type.
    if (body.length <= 10) {
      if (body.length <= 3) return body;
      if (body.length <= 6) return `(${body.slice(0, 3)}) ${body.slice(3)}`;
      return `(${body.slice(0, 3)}) ${body.slice(3, 6)}-${body.slice(6)}`;
    }
    if (body.length === 11 && body.startsWith("1")) {
      return `1 (${body.slice(1, 4)}) ${body.slice(4, 7)}-${body.slice(7)}`;
    }
    return body;
  }

  // International: +1 gets the NANP shape, everything else is grouped.
  if (body.startsWith("1")) {
    const nsn = body.slice(1);
    if (!nsn) return "+1";
    if (nsn.length <= 3) return `+1 ${nsn}`;
    if (nsn.length <= 6) return `+1 (${nsn.slice(0, 3)}) ${nsn.slice(3)}`;
    return `+1 (${nsn.slice(0, 3)}) ${nsn.slice(3, 6)}-${nsn.slice(6, 10)}${nsn.slice(10)}`;
  }

  // Generic international grouping: +CC then blocks of 3.
  const cc = body.slice(0, 2);
  const rest = body.slice(2);
  if (!rest) return `+${body}`;
  const groups = rest.match(/.{1,3}/g) || [];
  // Avoid an orphaned single trailing digit (…456 7 → …4567).
  if (groups.length > 1 && groups[groups.length - 1].length === 1) {
    const last = groups.pop() as string;
    groups[groups.length - 1] += last;
  }
  return `+${cc} ${groups.join(" ")}`;
}

interface DialpadProps {
  /** When false, keyboard/paste capture is disabled (e.g. tab not visible). */
  active?: boolean;
}

interface ResolvedCaller {
  name: string;
  /** "lead" | "contact" → id is a lead ID; "client" → id is a client ID. */
  type: "lead" | "client" | "contact";
  id: string;
  /** The E.164 number this match was resolved for (staleness guard). */
  phone: string;
}

export function Dialpad({ active = true }: DialpadProps) {
  const [value, setValue] = useState("");
  const holdTimerRef = useRef<number | null>(null);
  const heldRef = useRef(false);
  const [resolved, setResolved] = useState<ResolvedCaller | null>(null);
  const resolveSeqRef = useRef(0);

  const e164 = toE164(value);
  const canCall = !!e164;

  // Look up the typed number against known leads/clients/contacts once it
  // forms a complete dialable number. Debounced so we don't fire on every
  // keypress; unmatched numbers just resolve to null (no error surfaced).
  useEffect(() => {
    const seq = ++resolveSeqRef.current;
    // Clear any previous match immediately so a stale record can never be
    // shown for — or attached to — a different number.
    setResolved(null);
    if (!e164) return;
    const timer = window.setTimeout(async () => {
      try {
        const res = await fetch(`/api/dialer/resolve-caller?phone=${encodeURIComponent(e164)}`, {
          credentials: "include",
        });
        if (seq !== resolveSeqRef.current) return;
        if (!res.ok) {
          setResolved(null);
          return;
        }
        const data = await res.json();
        setResolved(data?.name ? { ...data, phone: e164 } : null);
      } catch {
        if (seq === resolveSeqRef.current) setResolved(null);
      }
    }, 350);
    return () => window.clearTimeout(timer);
  }, [e164]);

  const append = useCallback((ch: string) => {
    setValue((prev) => {
      // "+" is only meaningful as the very first character.
      if (ch === "+") return prev.length === 0 ? "+" : prev;
      return (prev + ch).slice(0, 20);
    });
  }, []);

  const backspace = useCallback(() => setValue((prev) => prev.slice(0, -1)), []);
  const clearAll = useCallback(() => setValue(""), []);

  const placeCall = useCallback(() => {
    if (!e164) return;
    // Only attach a record if the match belongs to the number being dialed.
    const match = resolved && resolved.phone === e164 ? resolved : null;
    const isLead = match?.type === "lead" || match?.type === "contact";
    const isClient = match?.type === "client";
    window.dispatchEvent(
      new CustomEvent("entertaincrm:dialer:open", {
        detail: {
          phone: e164,
          contactName: match?.name || null,
          leadId: isLead ? match!.id : null,
          clientId: isClient ? match!.id : null,
        },
      }),
    );
  }, [e164, resolved]);

  // Physical keyboard support. Ignored while the user is typing into a real
  // field somewhere else on the page.
  useEffect(() => {
    if (!active) return;
    const isTypingElsewhere = (target: EventTarget | null) => {
      const el = target as HTMLElement | null;
      if (!el || !el.tagName) return false;
      const tag = el.tagName.toLowerCase();
      return tag === "input" || tag === "textarea" || tag === "select" || el.isContentEditable;
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingElsewhere(e.target)) return;
      if (/^[0-9*#+]$/.test(e.key)) {
        e.preventDefault();
        append(e.key);
      } else if (e.key === "Backspace") {
        e.preventDefault();
        backspace();
      } else if (e.key === "Enter") {
        e.preventDefault();
        placeCall();
      }
    };

    const onPaste = (e: ClipboardEvent) => {
      if (isTypingElsewhere(e.target)) return;
      const text = e.clipboardData?.getData("text");
      if (!text) return;
      const cleaned = sanitizeDialInput(text);
      if (!cleaned) return;
      e.preventDefault();
      setValue(cleaned.slice(0, 20));
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("paste", onPaste);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("paste", onPaste);
    };
  }, [active, append, backspace, placeCall]);

  // Press-and-hold backspace clears the whole number.
  const startHold = () => {
    heldRef.current = false;
    holdTimerRef.current = window.setTimeout(() => {
      heldRef.current = true;
      clearAll();
    }, 600) as unknown as number;
  };
  const endHold = () => {
    if (holdTimerRef.current != null) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    if (!heldRef.current) backspace();
    heldRef.current = false;
  };
  const cancelHold = () => {
    if (holdTimerRef.current != null) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    heldRef.current = false;
  };

  useEffect(() => () => cancelHold(), []);

  return (
    <div className="space-y-3" data-testid="dialpad">
      {/* Number display */}
      <div className="rounded-lg border border-border/60 bg-muted/30 px-3 py-3 min-h-[56px] flex items-center justify-center">
        <span
          className={`font-mono text-xl tracking-wide text-center break-all ${value ? "text-foreground" : "text-muted-foreground/60"}`}
          data-testid="text-dialpad-number"
        >
          {formatDialDisplay(value) || "Enter a number"}
        </span>
      </div>

      {/* Matched contact (if the number belongs to a known record) */}
      {resolved && (
        <div
          className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground -mt-1"
          data-testid="text-dialpad-match"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          <span className="font-medium text-foreground truncate max-w-[220px]">{resolved.name}</span>
          <span className="capitalize">· {resolved.type}</span>
        </div>
      )}

      {/* Digit grid */}
      <div className="grid grid-cols-3 gap-2">
        {DIAL_KEYS.map((k) => (
          <Button
            key={k}
            type="button"
            variant="outline"
            className="h-14 flex flex-col items-center justify-center gap-0 select-none"
            onClick={() => append(k)}
            data-testid={`button-dialpad-${k === "*" ? "star" : k === "#" ? "pound" : k}`}
          >
            <span className="text-lg font-semibold leading-none">{k}</span>
            {KEY_LETTERS[k] && (
              <span className="text-[9px] tracking-[0.15em] text-muted-foreground leading-none mt-0.5">
                {KEY_LETTERS[k]}
              </span>
            )}
          </Button>
        ))}
      </div>

      {/* Actions */}
      <div className="grid grid-cols-3 gap-2 items-center">
        <Button
          type="button"
          variant="ghost"
          className="h-12 text-xs text-muted-foreground"
          onClick={() => append("+")}
          disabled={value.length > 0}
          data-testid="button-dialpad-plus"
        >
          +
        </Button>

        <Button
          type="button"
          className="h-12 bg-green-600 hover:bg-green-700 text-white"
          disabled={!canCall}
          onClick={placeCall}
          data-testid="button-dialpad-call"
        >
          <Phone className="h-4 w-4 mr-1.5" />
          Call
        </Button>

        <Button
          type="button"
          variant="ghost"
          className="h-12"
          disabled={!value}
          onPointerDown={startHold}
          onPointerUp={endHold}
          onPointerLeave={cancelHold}
          title="Backspace (hold to clear)"
          aria-label="Backspace (hold to clear)"
          data-testid="button-dialpad-backspace"
        >
          <Delete className="h-4 w-4" />
        </Button>
      </div>

      <p className="text-[11px] text-muted-foreground text-center">
        Type or paste a number, then press Call. Enter dials, hold ⌫ to clear.
      </p>
    </div>
  );
}
